import React, { useState, useEffect } from 'react';
import { recipeAPI, uploadAPI } from '../services/api';

// Parse ingredient with quantity (format: "quantity ingredient" or just "ingredient")
const parseIngredient = (ingredientText) => {
  if (!ingredientText) return { quantity: '', item: '' };
  // Try to split into quantity and item (assume first word/words are quantity)
  const parts = ingredientText.trim().split(/\s+(.+)/);
  if (parts.length > 1 && parts[1]) {
    return { quantity: parts[0], item: parts[1] };
  }
  return { quantity: '', item: ingredientText };
};

function RecipeModal({ recipe, onClose, onSave, onDelete }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    servings: '',
    prep_time: '',
    cook_time: '',
    total_time: '',
    rating: 0,
    notes: '',
    ingredients: [],
    instructions: [],
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (recipe) {
      setFormData({
        title: recipe.title || '',
        description: recipe.description || '',
        servings: recipe.servings || '',
        prep_time: recipe.prep_time || '',
        cook_time: recipe.cook_time || '',
        total_time: recipe.total_time || '',
        rating: recipe.rating || 0,
        notes: recipe.notes || '',
        ingredients: recipe.ingredients?.map(i => parseIngredient(i.item)) || [],
        instructions: recipe.instructions?.map(i => i.instruction) || [],
      });
    }
  }, [recipe]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...formData.ingredients];
    if (typeof newIngredients[index] === 'string') {
      // Convert old format to new format
      const parsed = parseIngredient(newIngredients[index]);
      newIngredients[index] = { ...parsed, [field]: value };
    } else {
      newIngredients[index] = { ...newIngredients[index], [field]: value };
    }
    setFormData(prev => ({ ...prev, ingredients: newIngredients }));
  };

  const addIngredient = () => {
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { quantity: '', item: '' }]
    }));
  };

  const removeIngredient = (index) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...formData.instructions];
    newInstructions[index] = value;
    setFormData(prev => ({ ...prev, instructions: newInstructions }));
  };

  const addInstruction = () => {
    setFormData(prev => ({
      ...prev,
      instructions: [...prev.instructions, '']
    }));
  };

  const removeInstruction = (index) => {
    setFormData(prev => ({
      ...prev,
      instructions: prev.instructions.filter((_, i) => i !== index)
    }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      await uploadAPI.uploadPhoto(file, recipe.id);
      await onSave();
    } catch (error) {
      console.error('Failed to upload photo:', error);
      alert('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Format ingredients with quantities
      const formattedIngredients = formData.ingredients
        .map(ing => {
          const ingObj = typeof ing === 'string' ? parseIngredient(ing) : ing;
          const item = ingObj.item.trim();
          const quantity = ingObj.quantity.trim();
          return item ? (quantity ? `${quantity} ${item}` : item) : null;
        })
        .filter(ing => ing !== null);
      
      await recipeAPI.update(recipe.id, {
        ...formData,
        ingredients: formattedIngredients,
        list_id: recipe.list_id
      });
      onSave();
    } catch (error) {
      console.error('Failed to save recipe:', error);
      alert('Failed to save recipe');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{recipe.title}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-section">
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>Servings</label>
                <input
                  type="text"
                  name="servings"
                  value={formData.servings}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Rating</label>
                <select
                  name="rating"
                  value={formData.rating}
                  onChange={handleChange}
                >
                  <option value="0">No rating</option>
                  <option value="1">★</option>
                  <option value="2">★★</option>
                  <option value="3">★★★</option>
                  <option value="4">★★★★</option>
                  <option value="5">★★★★★</option>
                </select>
              </div>

              <div className="form-group">
                <label>Prep Time</label>
                <input
                  type="text"
                  name="prep_time"
                  value={formData.prep_time}
                  onChange={handleChange}
                  placeholder="e.g., 15 mins"
                />
              </div>

              <div className="form-group">
                <label>Cook Time</label>
                <input
                  type="text"
                  name="cook_time"
                  value={formData.cook_time}
                  onChange={handleChange}
                  placeholder="e.g., 30 mins"
                />
              </div>
            </div>
          </div>

          <div className="modal-section">
            <h3>Ingredients</h3>
            {formData.ingredients.map((ingredient, index) => {
              const ing = typeof ingredient === 'string' ? parseIngredient(ingredient) : ingredient;
              return (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={ing.quantity}
                    onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
                    placeholder="Quantity (e.g., 2 cups)"
                    style={{ width: '150px', flexShrink: 0 }}
                  />
                  <input
                    type="text"
                    value={ing.item}
                    onChange={(e) => handleIngredientChange(index, 'item', e.target.value)}
                    placeholder="Ingredient name"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => removeIngredient(index)}
                  >
                    Remove
                  </button>
                </div>
              );
            })}
            <button type="button" className="btn btn-secondary" onClick={addIngredient}>
              + Add Ingredient
            </button>
          </div>

          <div className="modal-section">
            <h3>Instructions</h3>
            {formData.instructions.map((instruction, index) => (
              <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <textarea
                  value={instruction}
                  onChange={(e) => handleInstructionChange(index, e.target.value)}
                  placeholder={`Step ${index + 1}`}
                  style={{ flex: 1, minHeight: '60px' }}
                />
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => removeInstruction(index)}
                >
                  Remove
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary" onClick={addInstruction}>
              + Add Step
            </button>
          </div>

          <div className="modal-section">
            <h3>Notes</h3>
            <div className="form-group">
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Add your notes, modifications, or tips..."
              />
            </div>
          </div>

          <div className="modal-section">
            <h3>Photos</h3>
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              disabled={uploading}
            />
            {uploading && <span className="loading-spinner" style={{ marginLeft: '1rem' }}></span>}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-danger" onClick={onDelete}>
              Delete Recipe
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-success">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RecipeModal;
